import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ActionIcon, ActionIconProps, ComboboxItem, Group, GroupProps, Input, Stack, StackProps } from '@mantine/core';
import { useDidUpdate, useUncontrolled } from '@mantine/hooks';
import { If } from '@vision/ui/components';
import { UseLazyLoadSelectOptions } from '@vision/ui/hooks';
import { KeyValue } from '@vision/ui/interfaces';
import { ensureArray } from '@vision/ui/utils';
import React, { forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './KeyValuePairInput.module.css';
import {
  KeyValuePairInputPropsType,
  KeyValuePairInputSwitchCase,
  KeyValuePairInputType,
  KeyValuePairInputValueType,
} from './components';
import { KeyValuePairInputAddButton, KeyValuePairInputAddButtonProps } from './components/KeyValuePairInputAddButton';

export interface KeyValuePairInputProps {
  disabled?: boolean;
  keyError?: (index: number) => React.ReactNode;
  keyInputType?: KeyValuePairInputType;
  keyItemOptions?: ComboboxItem[];
  keyLabel?: string;
  keyPlaceholder?: string;
  keyProps?: KeyValuePairInputPropsType;
  keyValueInputMapping?: {
    keySelectedValue: string;
    lazyLoadOptions?: UseLazyLoadSelectOptions<any, any>;
    valueInputType?: KeyValuePairInputType;
    valueItemOptions?: ComboboxItem[];
  }[];
  label?: string;
  maxPairs?: number;
  multiple?: boolean;
  name: string;
  onChange: (value: KeyValue<KeyValuePairInputValueType, KeyValuePairInputValueType>[]) => void;
  value: KeyValue<KeyValuePairInputValueType, KeyValuePairInputValueType>[];
  valueError?: (index: number) => React.ReactNode;
  valueInputType?: KeyValuePairInputType;
  valueItemOptions?: ComboboxItem[];
  valueLabel?: string;
  valuePlaceholder?: string;
  valueProps?: KeyValuePairInputPropsType;
  enterKeyChangeClearValue?: boolean;
  addButtonProps?: ActionIconProps;
  removeButtonProps?: ActionIconProps;
  containerProps?: StackProps;
  keyValuePairProps?: GroupProps;
  leftIcon?: React.ReactNode;
  renderAddButton?: (props: KeyValuePairInputAddButtonProps) => React.ReactNode;
}

export interface KeyValuePairInputRef {
  clear: VoidFunction;
}

export const KeyValuePairInput = forwardRef(
  (
    {
      disabled = false,
      keyError,
      keyInputType = 'input',
      keyItemOptions = [],
      keyLabel,
      keyPlaceholder,
      keyProps,
      keyValueInputMapping,
      label,
      maxPairs = 10,
      multiple,
      name,
      onChange,
      value,
      valueError,
      valueInputType = 'input',
      valueItemOptions = [],
      valueLabel,
      valuePlaceholder,
      valueProps,
      enterKeyChangeClearValue = false,
      addButtonProps,
      removeButtonProps,
      containerProps,
      keyValuePairProps,
      leftIcon,
      renderAddButton,
    }: KeyValuePairInputProps,
    ref,
  ) => {
    const { t } = useTranslation();
    const [pairs, setPairs] = useUncontrolled<KeyValue<KeyValuePairInputValueType, KeyValuePairInputValueType>[]>({
      value,
      defaultValue: [{ key: '', value: '' }],
      onChange,
    });

    const handleAddPair = () => {
      setPairs([...pairs, { key: '', value: '' }]);
    };

    const handleRemovePair = (index: number) => {
      setPairs(pairs.filter((_, i) => i !== index));
    };

    const handleChange = (
      index: number,
      value: KeyValuePairInputValueType,
      key: KeyValuePairInputValueType,
      clearValue = false,
    ) => {
      setPairs(
        pairs.map((pair, i) => {
          if (i === index) {
            return {
              key,
              value: clearValue ? '' : value,
            };
          }

          return pair;
        }),
      );
    };

    useDidUpdate(() => {
      setPairs([pairs[0]]);
    }, [multiple]);

    useImperativeHandle(ref, () => ({
      clear: () => setPairs([{ key: '', value: '' }]),
    }));

    return (
      <>
        {label && <Input.Label>{label}</Input.Label>}
        <Stack gap={10} {...containerProps}>
          {pairs.map((pair, index) => {
            const found = ensureArray(keyValueInputMapping).find((item) => item.keySelectedValue === pair.key);
            const computedValueInputType = found?.valueInputType || valueInputType;
            const computedValueItemOptions = found?.valueItemOptions || valueItemOptions;

            return (
              <Group
                key={index}
                wrap="nowrap"
                gap={10}
                align={keyProps?.error || valueProps?.error ? 'center' : 'flex-end'}
                data-testid={`key-value-pair-${index}`}
                {...keyValuePairProps}
              >
                <Group gap={10} align="flex-start" wrap="nowrap">
                  {leftIcon}
                  <KeyValuePairInputSwitchCase
                    label={keyLabel}
                    placeholder={keyPlaceholder ?? t('key')}
                    inputType={keyInputType}
                    inputProps={keyProps}
                    itemOptions={keyItemOptions}
                    name={name}
                    index={index}
                    disabled={disabled}
                    handleChange={(index, value) => handleChange(index, pair.value, value, enterKeyChangeClearValue)}
                    value={pair.key}
                    error={keyError?.(index)}
                  />

                  <KeyValuePairInputSwitchCase
                    label={valueLabel}
                    placeholder={valuePlaceholder ?? t('value')}
                    inputType={computedValueInputType}
                    inputProps={valueProps}
                    itemOptions={computedValueItemOptions}
                    name={name}
                    index={index}
                    disabled={disabled}
                    handleChange={(index, value) => handleChange(index, value, pair.key)}
                    value={pair.value}
                    lazyLoadOptions={found?.lazyLoadOptions}
                    error={valueError?.(index)}
                  />
                </Group>

                <If value={multiple}>
                  <Group className={classes.keyValuePairInputActions} gap={10} wrap="nowrap">
                    <If value={index === pairs.length - 1}>
                      {renderAddButton ? (
                        renderAddButton({
                          disabled:
                            !pairs[pairs.length - 1].key || !pairs[pairs.length - 1].value || pairs.length === maxPairs,
                          handleAddPair,
                          addButtonProps,
                        })
                      ) : (
                        <KeyValuePairInputAddButton
                          disabled={
                            !pairs[pairs.length - 1].key || !pairs[pairs.length - 1].value || pairs.length === maxPairs
                          }
                          handleAddPair={handleAddPair}
                          addButtonProps={addButtonProps}
                        />
                      )}
                    </If>
                    <If value={pairs.length > 1}>
                      <ActionIcon
                        data-testid={`button-remove-key-value-pair-${index + 1}`}
                        disabled={disabled}
                        color="red"
                        variant="filled"
                        onClick={() => handleRemovePair(index)}
                        h={36}
                        miw={36}
                        {...removeButtonProps}
                      >
                        <FontAwesomeIcon icon={faTrashCan} />
                      </ActionIcon>
                    </If>
                  </Group>
                </If>
              </Group>
            );
          })}
        </Stack>
      </>
    );
  },
);
